/**
 * Step 1 of public registration — collect email and start the verification.
 *
 * Path: /register
 *
 * Includes a honeypot input (`website`) that real users never fill in — bots
 * commonly do, so a non-empty value short-circuits the submission.
 */
import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle2, Mail } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { PendoEvents } from '@/platform/analytics/pendoEvents'
import { publicRegistrationApi } from '../api/public'

export const RegistrationStartPage = (): JSX.Element => {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [email, setEmail] = useState('')
  const [honeypot, setHoneypot] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (honeypot) return
    setError(null)
    setBusy(true)
    try {
      await publicRegistrationApi.start({
        email,
        utm: {
          source: params.get('utm_source'),
          medium: params.get('utm_medium'),
          campaign: params.get('utm_campaign'),
          referrer: document.referrer || null,
        },
      })
      PendoEvents.registrationStep({ step: 'started' })
      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <CenteredCard title="Check your inbox" icon={<CheckCircle2 className="h-6 w-6" />}>
        <p>
          We sent a verification link to <strong>{email}</strong>. The link is good for
          24 hours.
        </p>
        <p className="text-sm text-muted-foreground">
          Didn’t receive it? Check your spam folder, then{' '}
          <button
            type="button"
            className="underline text-foreground"
            onClick={() => setSent(false)}
          >
            try again
          </button>
          .
        </p>
        <Button variant="ghost" className="mt-4" onClick={() => navigate('/login')}>
          Back to sign in
        </Button>
      </CenteredCard>
    )
  }

  return (
    <CenteredCard title="Create your workspace" icon={<Mail className="h-6 w-6" />}>
      <form onSubmit={onSubmit} className="space-y-3" noValidate>
        <label className="block text-sm font-medium" htmlFor="email">
          Work email
        </label>
        <Input
          id="email"
          type="email"
          required
          autoFocus
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {/* Honeypot — hidden from real users via CSS but visible to bots */}
        <input
          type="text"
          name="website"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
          aria-hidden="true"
        />
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <Button type="submit" disabled={busy || !email} className="w-full">
          {busy ? 'Sending…' : 'Send verification email'}
        </Button>
        <p className="text-xs text-muted-foreground">
          By continuing you agree to our terms. We’ll never sell your email.
        </p>
      </form>
    </CenteredCard>
  )
}

// ---------------------------------------------------------------------------
// Shared layout
// ---------------------------------------------------------------------------

function CenteredCard({
  title,
  icon,
  children,
}: {
  title: string
  icon?: React.ReactNode
  children: React.ReactNode
}): JSX.Element {
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {icon}
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">{children}</CardContent>
      </Card>
    </div>
  )
}
